import type { Experience } from '../types'

interface ExperienceCardProps {
  experience: Experience
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Present'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export function ExperienceCard({ experience }: ExperienceCardProps) {
  return (
    <div className="relative pl-8 pb-8 last:pb-0 border-l-2 border-slate-200 dark:border-slate-700 last:border-l-transparent">
      {/* Timeline dot */}
      <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-orange-500 border-4 border-white dark:border-slate-900" />

      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {experience.position}
          </h3>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {formatDate(experience.startDate)} - {formatDate(experience.endDate)}
          </span>
        </div>

        {/* Company and location */}
        <p className="text-orange-500 dark:text-orange-400 font-medium mb-3">
          {experience.company}
          <span className="text-slate-400 dark:text-slate-500 font-normal">
            {' '}
            &middot; {experience.location}
          </span>
        </p>

        {/* Description */}
        <p className="text-slate-600 dark:text-slate-300 mb-4">{experience.description}</p>

        {/* Achievements */}
        {experience.achievements.length > 0 && (
          <ul className="list-disc list-inside space-y-1 mb-4 text-slate-600 dark:text-slate-400">
            {experience.achievements.map((achievement, index) => (
              <li key={index}>{achievement}</li>
            ))}
          </ul>
        )}

        {/* Technologies */}
        <div className="flex flex-wrap gap-2">
          {experience.technologies.map((tech) => (
            <span
              key={tech}
              className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
